import CheckableTag from "antd/es/tag/CheckableTag";
import { formattedRate } from "../../../../../../components/common/Converter";

export const extractSalary = (salaryString) => {
  if (salaryString && typeof salaryString === "string") {
    const match = salaryString.match(/₱\s*([\d,]+(?:\.\d+)?)/);

    if (match) {
      return parseFloat(match[1].replace(/,/g, ""));
    }
  }
  return null;
};

export const getLastDayOfMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  return new Date(year, month, 0).getDate();
};

export const calculateFormattedTotal = (typeArray, matchingEntry) => {
  return formattedRate(
    typeArray
      .reduce((total, key) => {
        const value = parseFloat(matchingEntry?.[key]) || 0;
        return total + value;
      }, 0)
      .toString()
  );
};

export const calculateTotal = (amountsObject, formatFunction) => {
  return formatFunction(
    Object.values(amountsObject)
      .reduce((acc, value) => acc + value, 0)
      .toString()
  );
};

export const calculateAmount = (expression, rate) => {
  try {
    expression = expression.replace(/monthly_rate/g, rate);
    return Function(`"use strict"; return (${expression})`)();
  } catch (error) {
    console.error("Error evaluating amount:", error);
    return "";
  }
};

export const sumList = (list) =>
  list
    .reduce((total, amount) => total + parseFloat(amount.toFixed(2)), 0)
    .toFixed(2);

const calculateRates = (amount, hoursPerDay) => {
  const ratePerDay = formattedRate((amount / 22).toString());
  const ratePerHour = formattedRate((ratePerDay / hoursPerDay).toString());
  const ratePerMinute = formattedRate((ratePerHour / 60).toString());

  return { ratePerDay, ratePerMinute };
};

const calculateTotalAmount = (
  ratePerDay,
  ratePerMinute,
  sampleData1,
  sampleData2
) => {
  const totalAmount = sampleData1 * ratePerDay + sampleData2 * ratePerMinute;
  return Math.round(totalAmount * 100) / 100;
};

export const calculateResults = (
  salariesOrPera,
  sampleData1,
  sampleData2,
  hoursPerDay = 8
) => {
  if (Array.isArray(salariesOrPera)) {
    return salariesOrPera.map((salary) => {
      const { ratePerDay, ratePerMinute } = calculateRates(salary, hoursPerDay);
      return calculateTotalAmount(
        ratePerDay,
        ratePerMinute,
        sampleData1,
        sampleData2
      );
    });
  } else {
    const { ratePerDay, ratePerMinute } = calculateRates(
      salariesOrPera,
      hoursPerDay
    );
    return calculateTotalAmount(
      ratePerDay,
      ratePerMinute,
      sampleData1,
      sampleData2
    );
  }
};

const parseAndFormat = (value) => parseFloat(formattedRate(value.toString()));

export const calculatetotalExcess = (salaryString, allowanceList, item) => {
  let result = {};
  const salary = parseAndFormat(salaryString * 12);
  const employeeAllowances = allowanceList?.allowance[item.id] ?? [];

  if (employeeAllowances && Array.isArray(employeeAllowances)) {
    const gratuityAllowances = employeeAllowances.filter(
      (a) => a.allowance.toLowerCase() === "gratuity pay"
    );

    if (!result[item.id]) {
      result[item.id] = [];
    }

    gratuityAllowances.forEach((a) => {
      const basicGratuityTotal = parseAndFormat(salary + a.amount);
      const twentyPercent = parseAndFormat(basicGratuityTotal * 0.2);
      const totalEarned = parseAndFormat(basicGratuityTotal + twentyPercent);

      if (totalEarned > 250000) {
        result[item.id] = parseAndFormat(totalEarned - 250000);
      } else {
        result[item.id] = 0;
      }
    });
  }

  return result;
};

export const calculatethreePercent = (isGraduated, salaryString) => {
  let threePercent = 0;
  if (isGraduated === "YES") {
    threePercent = parseAndFormat(salaryString * 0.03);
  } else {
    threePercent = 0;
  }
  return threePercent;
};

export const calculateTwoPercent = (isSubmitted, salaryString, totalTaxJO) => {
  let result = 0;
  const firstRange = new Set([20000, 24000]);

  if (isSubmitted === "YES" && firstRange.has(salaryString)) {
    const twoPercent = parseAndFormat(totalTaxJO * 0.02);
    const threePercentage = parseAndFormat(twoPercent / 12);
    result = threePercentage;
  } else {
    result = 0;
  }
  return result;
};

export const calculateFivePercent = (isSubmitted, salaryString, totalTaxJO) => {
  let result = 0;
  const secondRange = 27000;
  if (isSubmitted === "YES" && salaryString >= secondRange) {
    const fivePercent = parseAndFormat(totalTaxJO * 0.05);
    const fivePercentage = parseAndFormat(fivePercent / 12);
    result = fivePercentage;
  } else {
    result = 0;
  }
  return result;
};

export const calculateTenPercent = (isSubmitted, totalTaxJO) => {
  let result = 0;
  if (isSubmitted === "NO") {
    const tenPercent = parseAndFormat(totalTaxJO * 0.1);
    const tenPercentage = parseAndFormat(tenPercent / 12);
    result = tenPercentage;
  }
  return result;
};

export const calculate = (initialValue, operations) => {
  return operations.reduce((result, { type, value }) => {
    const parsedValue = parseFloat(value) || 0;

    let intermediateResult;
    switch (type) {
      case "add":
        intermediateResult = result + parsedValue;
        break;
      case "subtract":
        intermediateResult = result - parsedValue;
        if (intermediateResult < 0) intermediateResult = 0;
        break;
      case "multiply":
        intermediateResult = result * parsedValue;
        break;
      case "divide":
        intermediateResult = parsedValue !== 0 ? result / parsedValue : result;
        break;
      default:
        intermediateResult = result;
    }

    return parseFloat(formattedRate(intermediateResult.toString()));
  }, parseFloat(initialValue) || 0);
};

export const calculateTaxLaw = (
  monthIndex,
  salaryString,
  taxTableList,
  item,
  allowanceList,
  totalContribution
) => {
  let result = 0;
  let total = 0;

  const employeeAllowances = allowanceList?.allowance[item.id] ?? [];

  if (employeeAllowances && Array.isArray(employeeAllowances)) {
    employeeAllowances.forEach((a) => {
      if (a.taxable === "YES") {
        total += a.amount;
        // const annualAmount = parseAndFormat(a.amount * 12);

        // if (annualAmount > a.excessAmount) {
        //   const finalResult = annualAmount - a.excessAmount;
        //   result += finalResult;
        // }
      }
      // else if (a.taxable === "NO") {
      //   total += a.amount;

      //   if (total > 90000) {
      //     const finalResult = parseAndFormat(total - 90000);
      //     result += finalResult;
      //   }
      // }
    });
  } else {
    result = 0;
  }

  const exccessOf = total - 90000;
  const netOfMandatory = salaryString - totalContribution;

  const salary = parseAndFormat(netOfMandatory * monthIndex);
  const salaryAndAllowance = parseAndFormat(salary + exccessOf);

  // if (salary === 0) return 0;
  //TRAIN LAW
  const applicableBracket = taxTableList.find(
    (bracket) =>
      salaryAndAllowance > bracket.from &&
      (salaryAndAllowance < bracket.to || bracket.to === 0)
  );

  // if (!applicableBracket) {
  //   return 0;
  // }

  const excess = parseAndFormat(
    salaryAndAllowance - (applicableBracket?.compensation ?? 0)
  );

  const taxOnExcess = parseAndFormat(
    (excess * (applicableBracket?.percent ?? 0)) / 100
  );

  const excessInitial = parseAndFormat(
    taxOnExcess + (applicableBracket?.initial ?? 0)
  );
  const totalTax = parseAndFormat(excessInitial / monthIndex);

  return {
    totalTax: !applicableBracket ? 0 : totalTax,
    taxOnExcess,
    totalAllowances: total,
    salary,
  };
};

export const calculateApTax = (
  monthIndex,
  payCode,
  taxTableList,
  totalAllowances,
  taxOnExcess,
  salary
) => {
  const forcastedPaycode = parseAndFormat(payCode * monthIndex);

  const addedAllowances = parseAndFormat(forcastedPaycode + totalAllowances);
  const resultDeducted = parseAndFormat(addedAllowances - 90000);

  const addedSalary = parseAndFormat(resultDeducted + salary);

  const applicableBracket = taxTableList.find(
    (bracket) =>
      addedSalary > bracket.from &&
      (addedSalary < bracket.to || bracket.to === 0)
  );

  if (!applicableBracket) {
    return 0;
  }

  const excess = parseAndFormat(addedSalary - applicableBracket.compensation);

  const getPercentage = parseAndFormat(
    (excess * applicableBracket.percent) / 100
  );

  const excessInitial = parseAndFormat(
    getPercentage + applicableBracket.initial
  );

  const deductedByTaxDue = parseAndFormat(excessInitial - taxOnExcess);

  const monthlyApTax = parseAndFormat(deductedByTaxDue / monthIndex);

  return monthlyApTax;
};

export const calculateAbsences = (absences) => {
  const absencesList = absences?.absences ?? 0;
  return absencesList;
};

export const getAPrate = (apRate) => {
  return apRate;
};

export const calculateDeducPera = (
  addAbsence,
  peraAda,
  counts,
  minstobeDeduc
) => {
  const firstCalculation = parseAndFormat((addAbsence * peraAda) / 22);
  const secondCalculation = parseAndFormat((counts * peraAda) / 22 / 2);
  const thirdCalculation = parseAndFormat(
    (minstobeDeduc * peraAda) / 22 / 8 / 60
  );
  const finalResult = parseAndFormat(
    firstCalculation + secondCalculation + thirdCalculation
  );

  return finalResult;
};

export const calculateDueAbsences = (
  addAbsence,
  salaryString,
  counts,
  minstobeDeduc
) => {
  const firstCalculation = parseAndFormat((addAbsence * salaryString) / 22);
  const secondCalculation = parseAndFormat((counts * salaryString) / 22 / 2);
  const thirdCalculation = parseAndFormat(
    (minstobeDeduc * salaryString) / 22 / 8 / 60
  );
  const finalResult = parseAndFormat(
    firstCalculation + secondCalculation + thirdCalculation
  );

  return finalResult;
};

export const getAugmentation = (payrollCode, payCode) => {
  const percentage = parseFloat(payrollCode);
  const amount = payCode ? payCode : 0;

  if (isNaN(percentage) || isNaN(amount)) {
    return null;
  }

  const result = (percentage / 100) * amount;

  return result;
};
